import { yupResolver } from "@hookform/resolvers/yup";
import { useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { useGetLoggedInUserDetails } from "../../../aAdminTabTeam2/hooksAndFunctions/customHooks/useGetLoggedInUserDetails";
import { useCreateGenericAccountMutation } from "../../../api/services/client/clientApis";
import { CreateGenericAccountParams } from "../../../api/services/client/models";
import { useGetUsersDetailByRoleMutation } from "../../../api/services/user/userApi";
import { STATUS_BADGE_TYPE } from "../../../components";
import {
  BE_ERROR_CODES,
  GENERIC_ACCOUNT_PURPOSE_LIST_SITE_CORE_VALUES,
  PAGE_URLS,
  ROLE_IDS,
} from "../../../helpers/constants";
import { getDateFormatByCountry } from "../../../helpers/constants/countryDateFormat";
import { RefDropdownHandle } from "../../../helpers/types/common";
import {
  convertToUTCFormat,
  getFieldUndefinedForPayloadParams,
  getParsedBEErrors,
  getValueOrBlank,
  hasKey,
  isNotEmpty,
} from "../../../helpers/utils";
import useAccordion from "../../../hooks/useAccordion";
import { useSiteCore } from "../../../hooks/useSiteCore";
import { useToast } from "../../../hooks/useToast";
import { toggleLoader } from "../../../redux/slices/appState/appStateSlice";
import { getMappedFeatures } from "../helper";
import { CreateGenericAccountProps } from "./CreateGenericAccount";
import {
  CreateGenericAccountFormKey,
  FeaturesCheckBoxes,
} from "./modelsAndSchemas/Models";
import { CreateGenericAccountValidationSchema } from "./modelsAndSchemas/ValidationSchema";
import { createGenericAccounts } from "./siteCore/SiteCoreKeys";

const initialAccordionStatus = {
  all: true,
  basicDetails: true,
  configuration: true,
};

export const useCreateGenericAccountHook = ({
  genericAccountData,
}: CreateGenericAccountProps) => {
  const dispatch = useDispatch();
  const { displayToast } = useToast();
  const {
    fetchLabel,
    commonLabels,
    fetchComponentTitle,
    commonValidationMessages,
  } = useSiteCore();

  const {
    createGenericAccountBasicDetailsTitle,
    createGenericAccountConfigurationTitle,
    createGenericAccountGenericAccountHeaderTitle,
    genericAccountCreatedMessage,
    areYouSureYouWantToCreateGenericAccount,
    thisAccountNameAlreadyExists,
    thisRegistrationCodeAlreadyExists,
    expirationDateMustBeGreaterThanStartDate,
    startDateMustBeLesserThanExpirationDate,
  } = createGenericAccounts;

  const getHeaderLabel = (key: string) => {
    return fetchLabel(createGenericAccountGenericAccountHeaderTitle, key);
  };

  const getBasicDetailsLabel = (key: string) => {
    return fetchLabel(createGenericAccountBasicDetailsTitle, key);
  };

  const navigate = useNavigate();
  const location = useLocation();

  const { loggedInUser } = useGetLoggedInUserDetails();
  const userCountryDateFormat = getDateFormatByCountry(
    loggedInUser?.countryCode ?? "",
  );

  const deliveryOfficeRef = useRef<RefDropdownHandle>(null);
  const {
    accordionStatus,
    handleIndividualAccordionChange,
    handleExpandCollapseAllChange,
  } = useAccordion(initialAccordionStatus);

  const [showCancelConfirmationModal, setShowCancelConfirmationModal] =
    useState(false);
  const [showSaveUpdateConfirmationModal, setShowSaveUpdateConfirmationModal] =
    useState(false);

  const methods = useForm({
    mode: "onBlur",
    defaultValues: {
      [CreateGenericAccountFormKey.genericAccountName]: "",
      [CreateGenericAccountFormKey.purpose]: "",
      [CreateGenericAccountFormKey.clientId]: "",
      [CreateGenericAccountFormKey.genericRegistrationCode]: "",
      [CreateGenericAccountFormKey.startDate]: "",
      [CreateGenericAccountFormKey.expirationDate]: "",
      [CreateGenericAccountFormKey.numberOfAccounts]: "",
      [CreateGenericAccountFormKey.programDuration]: "",
      [CreateGenericAccountFormKey.requestedBy]: "",
      [CreateGenericAccountFormKey.nextView]: "",
      [CreateGenericAccountFormKey.country]: "",
      [CreateGenericAccountFormKey.deliveryOffice]: [],
      [CreateGenericAccountFormKey.nextProgramClass]: "",
      [CreateGenericAccountFormKey.features]: [],
      [CreateGenericAccountFormKey.status]: STATUS_BADGE_TYPE.active,
    },
    resolver: yupResolver(
      CreateGenericAccountValidationSchema({
        ...commonValidationMessages,
        expirationDateMustBeGreaterThanStartDateErrorMessage:
          getBasicDetailsLabel(expirationDateMustBeGreaterThanStartDate),
        startDateMustBeLesserThanExpirationDateErrorMessage:
          getBasicDetailsLabel(startDateMustBeLesserThanExpirationDate),
      }),
    ),
  });
  const { getValues, handleSubmit, formState, setError } = methods;

  const {
    mutateAsync: getUsersDetailsByRole,
    data: usersList,
    isPending: isUsersListPending,
    isError: isUsersListError,
  } = useGetUsersDetailByRoleMutation();

  useEffect(() => {
    getUsersDetailsByRole({
      roleIds: [
        ROLE_IDS.sales,
        ROLE_IDS.admin,
        ROLE_IDS.corporateAdmin,
        ROLE_IDS.outreachConsultantLead,
        ROLE_IDS.projectManager,
        ROLE_IDS.projectCoOrdinator,
      ],
    });
  }, []);

  const genericAccountId = useMemo(
    () => genericAccountData?.id,
    [genericAccountData],
  );
  const from = location?.state?.from;

  const {
    mutate: createGenericAccount,
    isPending,
    data: createGenericAccountData,
    isSuccess: isCreateGenericAccountSuccess,
    isError: isCreateGenericAccountError,
    error: createGenericAccountError,
  } = useCreateGenericAccountMutation();

  useEffect(() => {
    dispatch(
      toggleLoader({
        isVisible: isUsersListPending,
        message: commonLabels.loadingPleaseWait,
      }),
    );
    return () => {
      dispatch(toggleLoader({ isVisible: false }));
    };
  }, [isUsersListPending]);

  useEffect(() => {
    if (isUsersListError) {
      displayToast(commonLabels.someThingWentWrongPleaseTryAgainLater, "error");
    }
  }, [isUsersListError]);

  const setErrorToForm = (
    key: keyof typeof CreateGenericAccountFormKey &
      keyof typeof formState.errors,
    errorMessage: string,
  ) => {
    setError(key, {
      type: "custom",
      message: errorMessage,
    });
  };

  const showUniqueCheckError = (errorCode: string) => {
    switch (errorCode) {
      case BE_ERROR_CODES.CREATE_GENERIC_ACCOUNT_UNIQUE_NAME:
        setErrorToForm(
          CreateGenericAccountFormKey.genericAccountName,
          getBasicDetailsLabel(thisAccountNameAlreadyExists),
        );
        break;
      case BE_ERROR_CODES.CREATE_GENERIC_ACCOUNT_UNIQUE_REGISTRATION_CODE:
        setErrorToForm(
          CreateGenericAccountFormKey.genericRegistrationCode,
          getBasicDetailsLabel(thisRegistrationCodeAlreadyExists),
        );
        break;

      default:
        break;
    }
  };

  useEffect(() => {
    if (isCreateGenericAccountError && createGenericAccountError) {
      const errorObj = getParsedBEErrors(createGenericAccountError)[0];
      if (isNotEmpty(errorObj) && hasKey(errorObj, "errorCode")) {
        showUniqueCheckError(errorObj.errorCode);
      }
    }
  }, [isCreateGenericAccountError, createGenericAccountError]);

  useEffect(() => {
    dispatch(
      toggleLoader({
        isVisible: isPending,
      }),
    );
    return () => {
      dispatch(toggleLoader({ isVisible: false }));
    };
  }, [isPending]);

  useEffect(() => {
    if (isCreateGenericAccountSuccess) {
      if (deliveryOfficeRef.current) {
        deliveryOfficeRef.current?.resetValues();
      }
      displayToast(getHeaderLabel(genericAccountCreatedMessage), "success");
      navigateToViewGenericAccount(createGenericAccountData.data.id);
    }
  }, [isCreateGenericAccountSuccess]);

  const navigateToViewGenericAccount = (accountId?: string) => {
    navigate(PAGE_URLS.VIEW_GENERIC_ACCOUNT, {
      replace: true,
      state: {
        genericAccountId: accountId ?? genericAccountId,
        from: from,
      },
    });
  };

  const navigateBack = () => {
    if (from) {
      navigateToViewGenericAccount();
    } else {
      navigate(-1);
    }
  };

  const handleCancelClick = () => {
    if (!!isNotEmpty(formState.touchedFields)) {
      setShowCancelConfirmationModal(true);
      return;
    }
    navigateBack();
  };

  const handleCancelModalPrimaryButtonClick = () => {
    setShowCancelConfirmationModal(false);
    navigateBack();
  };

  const checkErrorsAndShowModal = () => {
    if (isNotEmpty(formState.errors)) {
      if (
        formState.errors.genericAccountName ||
        formState.errors.genericRegistrationCode
      ) {
        return;
      }
      handleSaveModalPrimaryButtonClick();
      return;
    }
    setShowSaveUpdateConfirmationModal(true);
  };

  const handleSubmitClick = () => {
    checkErrorsAndShowModal();
  };

  const handleSaveModalPrimaryButtonClick = () => {
    setShowSaveUpdateConfirmationModal(false);
    handleSubmit(callCreateGenericAccountAPI)();
  };

  const getCreateGenericAccountParams = () => {
    const {
      genericAccountName,
      clientId,
      purpose,
      startDate,
      genericRegistrationCode,
      numberOfAccounts,
      expirationDate,
      requestedBy,
      programDuration,
      country,
      nextView,
      nextProgramClass,
      deliveryOffice,
      status,
      features,
    } = getValues();

    const updatedFeatures = getMappedFeatures(features as FeaturesCheckBoxes[]);

    let matchedUser;
    if (requestedBy && isNotEmpty(requestedBy)) {
      matchedUser = usersList?.find((user: any) => user.email === requestedBy);
    }

    const params: CreateGenericAccountParams = {
      name: genericAccountName,
      registrationCode: genericRegistrationCode,
      purposeId: purpose,
      ...(purpose !== GENERIC_ACCOUNT_PURPOSE_LIST_SITE_CORE_VALUES.TEST_ACCESS
        ? getFieldUndefinedForPayloadParams({ clientId })
        : {}),
      startDate: convertToUTCFormat(startDate, userCountryDateFormat),
      expirationDate: convertToUTCFormat(
        expirationDate,
        userCountryDateFormat,
        true,
      ),
      programDuration,
      numberOfAccounts,
      ...(requestedBy && {
        requestedBy,
        requestedByUserId: getValueOrBlank(matchedUser?.id),
      }),
      countryId: country,
      solutionOfferingId: nextView,
      programClassId: nextProgramClass,
      deliveryOfficeIds: deliveryOffice,
      status: status,
      features: updatedFeatures,
    };

    return params;
  };

  const callCreateGenericAccountAPI = () => {
    createGenericAccount(getCreateGenericAccountParams());
  };

  const handleCancelModalClose = () => {
    if (showCancelConfirmationModal || showSaveUpdateConfirmationModal) {
      setShowCancelConfirmationModal(false);
      setShowSaveUpdateConfirmationModal(false);
    }
  };

  const getConfirmationLabel = () =>
    getHeaderLabel(areYouSureYouWantToCreateGenericAccount);

  return {
    // hooks
    methods,
    deliveryOfficeRef,
    accordionStatus,
    handleIndividualAccordionChange,
    handleExpandCollapseAllChange,
    // state
    showCancelConfirmationModal,
    showSaveUpdateConfirmationModal,
    // handlers
    handleCancelClick,
    handleSubmitClick,
    handleCancelModalPrimaryButtonClick,
    handleCancelModalClose,
    handleSaveModalPrimaryButtonClick,
    // data
    usersList,
    commonLabels,
    createGenericAccounts,
    fetchComponentTitle,
    fetchLabel,
    //labels
    genericAccountBasicDetailsTitle: fetchComponentTitle(
      createGenericAccountBasicDetailsTitle,
    ),
    genericAccountConfigurationTitle: fetchComponentTitle(
      createGenericAccountConfigurationTitle,
    ),
    confirmationToCreateGenericAccountLabel: getConfirmationLabel(),
  };
};
